import React, { useEffect, useRef, useState } from 'react';

// ---------- Minimal SceneManifest types (mirrors backend artifacts) ----------

export interface ManifestAsset {
  id: string;
  src: string;
  rgba: true;
  w: number;
  h: number;
  metadata?: {
    role: string;
    description: string;
    prompt: string;
    originalBbox?: [number, number, number, number];
    classification?: 'background' | 'foreground-decor' | 'subject-graphic' | 'border-frame' | 'text-overlay' | 'unknown';
    usageSuggestions?: {
      recommendedAnimations: string[];
      layerOrder: 'background' | 'middle' | 'foreground' | 'text-overlay';
      canOverlapText: boolean;
      transparencyIntent: string;
    };
    placementGuidelines?: {
      recommendedAnchors: string[];
      relativeScale: number; // Estimated scale relative to canvas width (e.g. 0.4 = 40%)
      alignmentConstraints: string;
    };
    originalReferenceContext?: {
      relativeZOrder: number;
      spatialIntent: string;
    };
  };
}

export interface ManifestLayer {
  id: string;
  assetId?: string;
  textSlotKey?: string;
  z: number;
  anchor?: string;
  fullBleed?: boolean;
}

export interface ManifestTextSlot {
  key: string;
  z: number;
  default?: string;
  editable: boolean;
  required: boolean;
  dynamic?: string;
  x?: number;
  y?: number;
  fontSize?: number;
  color?: string;
  align?: string;
  fontWeight?: string;
}

export interface AnimationBinding {
  target: string;
  type: string;
  provider: string;
  params: Record<string, unknown>;
  supported: boolean;
}

export interface SceneManifest {
  manifestVersion: '1.0';
  themeName: string;
  canvas: { w: number; h: number; safeArea: [number, number, number, number] };
  assets: ManifestAsset[];
  layers: ManifestLayer[];
  textSlots: ManifestTextSlot[];
  bindings: AnimationBinding[];
  provenance: { engine: 'type-b'; generatedAt: string; providers: Record<string, string>; dryRun: boolean };
}

// ---------- Text values editable by the user ----------

export type TextValues = Record<string, string>;

// ---------- CSS animation keyframes injected once ----------

const KEYFRAMES = `
@keyframes typeb-bloom {
  from { transform: scale(var(--bloom-from, 0.7)); opacity: 0.5; }
  to   { transform: scale(1); opacity: 1; }
}
@keyframes typeb-sway {
  0%,100% { transform: rotate(calc(var(--sway-deg, 3) * 1deg)); }
  50%      { transform: rotate(calc(var(--sway-deg, 3) * -1deg)); }
}
@keyframes typeb-glow {
  0%,100% { opacity: var(--glow-min, 0.4); }
  50%      { opacity: var(--glow-max, 1.0); }
}
@keyframes typeb-drift {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(var(--drift-x, 20px), var(--drift-y, -30px)); }
}
@keyframes typeb-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
`;

function injectKeyframes() {
  if (document.getElementById('typeb-keyframes')) return;
  const s = document.createElement('style');
  s.id = 'typeb-keyframes';
  s.textContent = KEYFRAMES;
  document.head.appendChild(s);
}

// ---------- Derive CSS animation string from a binding ----------

function bindingToCSS(b: AnimationBinding): React.CSSProperties {
  if (!b.supported) return {};
  const p = b.params as Record<string, number>;
  switch (b.type) {
    case 'bloom':
      return {
        '--bloom-from': String(p.from ?? 0.7),
        animation: `typeb-bloom ${p.duration ?? 0.8}s ease-out forwards`,
        animationDelay: `${p.delay ?? 0}s`,
      } as React.CSSProperties;
    case 'sway':
      return {
        '--sway-deg': String(p.deg ?? 3),
        transformOrigin: 'top center',
        animation: `typeb-sway ${(p.periodMs ?? 4000) / 1000}s ease-in-out infinite`,
      } as React.CSSProperties;
    case 'glow':
      return {
        '--glow-min': String(p.minOpacity ?? 0.4),
        '--glow-max': String(p.maxOpacity ?? 1.0),
        animation: `typeb-glow ${(p.periodMs ?? 2600) / 1000}s ease-in-out infinite`,
      } as React.CSSProperties;
    case 'drift':
      return {
        '--drift-x': `${p.dx ?? 20}px`,
        '--drift-y': `${p.dy ?? -30}px`,
        animation: `typeb-drift ${(p.periodMs ?? 6000) / 1000}s ease-in-out infinite`,
      } as React.CSSProperties;
    case 'fade-in':
      return {
        animation: `typeb-fade-in ${p.duration ?? 0.6}s ease-out forwards`,
        animationDelay: `${p.delay ?? 0.1}s`,
        opacity: 0,
      } as React.CSSProperties;
    default:
      return {};
  }
}

// Map layer id → combined CSS from all its bindings
function buildAnimationMap(bindings: AnimationBinding[]): Record<string, React.CSSProperties> {
  const map: Record<string, React.CSSProperties> = {};
  for (const b of bindings) {
    map[b.target] = { ...(map[b.target] ?? {}), ...bindingToCSS(b) };
  }
  return map;
}

// ---------- Anchor → absolute positioning within canvas ----------

function anchorStyles(anchor: string | undefined, fullBleed?: boolean): React.CSSProperties {
  if (fullBleed) return { position: 'absolute', inset: 0, width: '100%', height: '100%' };
  switch (anchor) {
    case 'top-left':     return { position: 'absolute', top: 0, left: 0, width: '40%' };
    case 'top-right':    return { position: 'absolute', top: 0, right: 0, width: '40%', transform: 'scaleX(-1)' };
    case 'bottom-left':  return { position: 'absolute', bottom: 0, left: 0, width: '40%', transform: 'scaleY(-1)' };
    case 'bottom-right': return { position: 'absolute', bottom: 0, right: 0, width: '40%', transform: 'scale(-1)' };
    case 'right':        return { position: 'absolute', top: '30%', right: 0, width: '20%' };
    case 'left':         return { position: 'absolute', top: '30%', left: 0, width: '20%' };
    default:             return { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '30%' };
  }
}

// ---------- Main component ----------

interface TypeBRendererProps {
  manifest: SceneManifest;
  textValues?: TextValues;      // override text slot defaults
  scale?: number;               // 0..1 — scales canvas to fit container
  className?: string;
}

export function TypeBRenderer({ manifest, textValues = {}, scale = 1, className = '' }: TypeBRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [computedScale, setComputedScale] = useState(scale);

  useEffect(() => { injectKeyframes(); }, []);

  // Auto-fit to container width if scale not explicitly provided
  useEffect(() => {
    if (scale !== 1) return;
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      if (w > 0) setComputedScale(w / manifest.canvas.w);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [scale, manifest.canvas.w]);

  const animMap = buildAnimationMap(manifest.bindings);
  const assetById = Object.fromEntries(manifest.assets.map((a) => [a.id, a]));

  const sortedLayers = [...manifest.layers].sort((a, b) => a.z - b.z);
  const textSlotByKey = Object.fromEntries(manifest.textSlots.map((s) => [s.key, s]));

  const canvasW = manifest.canvas.w;
  const canvasH = manifest.canvas.h;

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}
      style={{ width: '100%', aspectRatio: `${canvasW} / ${canvasH}` }}>
      {/* Scaled inner canvas */}
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: canvasW, height: canvasH,
        transform: `scale(${computedScale})`,
        transformOrigin: 'top left',
      }}>
        {sortedLayers.map((layer) => {
          const asset = layer.assetId ? assetById[layer.assetId] : null;
          const anim = animMap[layer.id] ?? {};
          
          // Auto-detect if this asset is a full-frame layer matching the background dimensions
          const bgAsset = assetById['bg'];
          const isFullFrame = asset && bgAsset && asset.w === bgAsset.w && asset.h === bgAsset.h;
          
          const pos = (layer.fullBleed || isFullFrame)
            ? ({ position: 'absolute', inset: 0, width: '100%', height: '100%' } as React.CSSProperties)
            : anchorStyles(layer.anchor, layer.fullBleed);

          if (asset) {
            return (
              <img
                key={layer.id}
                src={asset.src}
                alt=""
                style={{ ...pos, ...anim, objectFit: 'cover' }}
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            );
          }

          if (layer.textSlotKey) {
            const slot = textSlotByKey[layer.textSlotKey];
            const value = textValues[layer.textSlotKey] ?? slot?.default ?? '';
            
            const styles: React.CSSProperties = {
              ...pos,
              ...anim,
              zIndex: layer.z,
              fontSize: slot?.fontSize ? `${slot.fontSize}px` : '44px',
              color: slot?.color || '#2d1e0e',
              fontWeight: slot?.fontWeight === 'bold' ? 'bold' : 'normal',
              textAlign: (slot?.align || 'center') as any,
              whiteSpace: 'pre-line',
            };

            return (
              <div key={layer.id} style={styles}
                className="flex items-center justify-center text-center font-serif">
                <span>{value}</span>
              </div>
            );
          }

          return null;
        })}

        {/* Text slots that don't have a matching layer (render dynamically using their x, y and styling) */}
        {manifest.textSlots.map((slot) => {
          const hasLayer = manifest.layers.some((l) => l.textSlotKey === slot.key);
          if (hasLayer) return null;
          const value = textValues[slot.key] ?? slot.default ?? '';
          if (!value) return null;
          
          const styles: React.CSSProperties = {
            position: 'absolute',
            left: `${slot.x}%`,
            top: `${slot.y}%`,
            transform: 'translate(-50%, -50%)',
            zIndex: slot.z,
            fontSize: slot.fontSize ? `${slot.fontSize}px` : '44px',
            color: slot.color || '#2d1e0e',
            fontWeight: slot.fontWeight === 'bold' ? 'bold' : 'normal',
            textAlign: (slot.align || 'center') as any,
            width: '90%',
            whiteSpace: 'pre-line',
          };

          return (
            <div key={slot.key} style={styles} className="text-center font-serif">
              {value}
            </div>
          );
        })}
      </div>
    </div>
  );
}
